import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ListPartnershipsRequest, ListPartnershipsResponse, Partnership, PartnershipStatus } from 'identity-webapi-client';
import { Company, Product2, Range, UpdateProductRangeAuthorisedCompanyRequest, UpdateProductRangeAuthorisedCompanyResponse } from 'situview-webapi-client';
import {
  CreateRangeRequest, CreateRangeResponse, GetRangeRequest, GetRangeResponse,
  ResourceType, UpdateRangeRequest, UpdateRangeResponse, UpdateProductRangeRequest,
  UpdateProductRangeResponse
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, CompanyViewService, ConfigService, PartnershipService, ProductService, RangeService, StateService } from 'src/app/services';
import { MatTableDataSource } from '@angular/material/table';
import { IListProduct, IListProduct2 } from '../../products/products-list/products-list.component';
import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { fromEvent, interval, of } from 'rxjs';
import { concatMap, debounceTime, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'ranges-add-edit',
  templateUrl: './ranges-add-edit.component.html',
  styleUrls: ['./ranges-add-edit.component.less']
})

export class RangesAddEditComponent extends SituBaseComponent implements AfterViewInit {
  rangeForm: FormGroup;
  rangeName: string | undefined;
  selectedRange: Range | undefined;
  submitted = false;
  isAddMode: boolean = false;
  id: string = '';
  copyRange: Range | undefined;
  isDarkMode: boolean = true;
  isRangeModified: boolean = false;
  isPartnershipModified: boolean = false;

  /* Search */
  pageSize = 8;
  pageLength = 8;

  /* Data */
  allCompaniesMap: Map<string, Company> | undefined;

  rangeDataSource = new MatTableDataSource<Range>();
  searchTerm = "";
  shareMode: boolean = false;

  baseInRangeIds: string[] = [];
  inRange: IListProduct2[] = [];
  outRange: IListProduct2[] = [];
  basePartnershipIds: string[] = [];
  partnerships: IListPartnership[] = [];
  productsDataSource = new MatTableDataSource<IListProduct2>();

  readonly situViewUrl: string;

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private rangeService: RangeService,
    private productService: ProductService,
    private partnershipService: PartnershipService,
    private configService: ConfigService,
    private companyViewService: CompanyViewService) {
      super(authService, stateService);
      this.id = route.snapshot.params['id'];
      this.situViewUrl = configService.config.situViewWebApiBaseUrl;
      this.isAddMode = !this.id;
      this.rangeForm = this.formBuilder.group({
        id: [],
        name: [null, Validators.required]
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.isAddMode) {
      this._subscriptions.push(
        this.route.queryParams.subscribe(params => {
          if (params["range"]) {
            this.copyRange = JSON.parse(params['range']) as Range;
          }
        })
      );
    }
    this._subscriptions.push(this.rangeForm.controls.name.valueChanges.subscribe(x => {
      this.breadcrumbService.setParam('rangeName', x);
    }));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.observer && this.showMoreProductsMarker?.nativeElement) {
      this.observer.unobserve(this.showMoreProductsMarker.nativeElement);
    }
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  protected onSelectedCompanyChange(): void {
    if (!this.isAddMode) {
      this.loadRange()
        .then(() => {
          this.loadProducts();
        });
    } else {
      this.breadcrumbService.setParam('rangeName', "");
      this.rangeForm.removeControl("id");
      if (this.copyRange) {
        this.rangeForm.patchValue(this.copyRange);
      }
    }
  }

  get form() { return this.rangeForm.controls; }

  canBeEdited(range: Range) {
    return !range?.logicalExpression;
  }

  private companyNameCache: Map<string, Promise<string | undefined>> = new Map();
  getCompanyName(companyId: string | undefined): Promise<string | undefined> {
    if (!companyId) return Promise.resolve(undefined);

    if (!this.companyNameCache.has(companyId)) {
      const companyNamePromise = this.companyViewService.getCompanyName(companyId);
      this.companyNameCache.set(companyId, companyNamePromise);
    }

    return this.companyNameCache.get(companyId)!;
  }

  private companyLogoCache: Map<string, Promise<string | undefined>> = new Map();
  getCompanyLogo(companyId: string | undefined): Promise<string | undefined> {
    if (!companyId) return Promise.resolve(undefined);

    if (!this.companyLogoCache.has(companyId)) {
      const companyNamePromise = this.companyViewService.getCompanyLogo(companyId);
      this.companyLogoCache.set(companyId, companyNamePromise);
    }

    return this.companyLogoCache.get(companyId)!;
  }

  @ViewChild('showMoreProductsMarker') showMoreProductsMarker: ElementRef | undefined;
  private observer: IntersectionObserver | undefined;
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMoreProductsToShow()) {
          this.showMoreProducts();
        }
      });
    });

    if (this.showMoreProductsMarker?.nativeElement) {
      this.observer.observe(this.showMoreProductsMarker.nativeElement);
    }
  }

  showMoreProducts() {
    const incrementSize = 2;
    const intervalTime = 200;
    const targetPageLength = this.pageLength + this.pageSize;
    this._subscriptions.push(interval(intervalTime).pipe(
      takeWhile(() => this.pageLength < targetPageLength && this.pageLength < this.outRange.length),
      concatMap(() => {
        const nextPageLength = Math.min(this.pageLength + incrementSize, targetPageLength, this.outRange.length);
        return of(nextPageLength);
      })
    ).subscribe((nextPageLength) => {
      this.pageLength = nextPageLength;
    }));
  }

  hasMoreProductsToShow(): boolean {
    return this.outRange.length > this.pageLength;
  }

  onSubmit() {
    this.submitted = true;

    if (this.rangeForm.invalid) {
      this.submitted = false;
      return;
    }

    if (this.isAddMode) {
      this.addRange();
    } else {
      this.editRange();
    }
  }

  resetProduct() {
    if (this.isAddMode) {
      this.rangeForm.reset();
    } else {
      this.rangeForm.reset();
      this.loadRange();
    }
  }

  toggleShareMode() {
    this.shareMode = !this.shareMode;
    if (this.shareMode) {
      this.loadPartnerships();
      this.checkIfRangeModified();
    }
  }

  showProductsView() {
    this.router.navigate(['/products/ranges']);
  }

  selectProduct(product: IListProduct2) {
    if (!this.shareMode) {
      product.selected = !product.selected;
    }
  }

  selectAllProducts(isInRange: boolean) {
    return isInRange ?
      this.inRange.map(x => x.selected = true) :
      this.outRange.map(x => x.selected = true)
  }

  clearAllSelectedProducts(isInRange: boolean) {
    return isInRange ?
      this.inRange.map(x => x.selected = false) :
      this.outRange.map(x => x.selected = false)
  }

  productSelectedInList(isInRange: boolean) {
    return isInRange ?
      this.inRange.some(x => x.selected) :
      this.outRange.some(x => x.selected)
  }
  
  getProductsSelected(isInRange: boolean) {
    return isInRange ?
      this.inRange.filter(x => x.selected)?.length :
      this.outRange.filter(x => x.selected)?.length;
  }
  
  getProducts(isInRange: boolean) {
    return isInRange ?
      this.inRange?.length :
      this.outRange?.length
  }

  selectPartner(partnership: IListPartnership) {
    partnership.selected = !partnership.selected;
    this.checkIfPartnershipModified();
  }

  selectAllPartners() {
    this.partnerships.map(x => x.selected = true);
    this.checkIfPartnershipModified();
  }

  clearAllSelectedPartners() {
    this.partnerships.map(x => x.selected = false);
    this.checkIfPartnershipModified();
  }

  partnershipSelectedInList() {
    return this.partnerships.some(x => x.selected)
  }

  getPartnershipsSelected() {
    return this.partnerships.filter(x => x.selected)?.length;
  }

  transferList(isInRange: boolean) {
    if (isInRange) {
      this.inRange.filter(x => x.selected).forEach(product => {
        this.outRange.push(product);
        this.inRange = this.inRange.filter(inRange => inRange.id !== product.id);
      });
    } else {
      this.outRange.filter(x => x.selected).forEach(product => {
        this.inRange.push(product);
        this.outRange = this.outRange.filter(outRange => outRange.id !== product.id);
      });
    }
    this.checkIfRangeModified();
  }

  drop(event: CdkDragDrop<IListProduct[]>) {
    if (event.previousContainer !== event.container) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.checkIfRangeModified();
    }
  };

  checkIfRangeModified() {
    this.isRangeModified = this.baseInRangeIds.length !== this.inRange.length ||
      !this.baseInRangeIds.every(product => this.inRange.map(x => x.id).includes(product))
  }

  checkIfPartnershipModified() {
    this.isPartnershipModified = this.basePartnershipIds.length !== this.getPartnershipsSelected() ||
      !this.basePartnershipIds.every(partnership => this.getOtherCompanyIds().includes(partnership))
  }

  updateProductRange() {
    let request = new UpdateProductRangeRequest();
    request.rangeId = this.id;
    request.companyId = this.selectedCompany?.id;
    request.productIds = this.inRange.map(x => x.id as string) ?? [];

    this.rangeService.UpdateProductRange(request)
      .then((x: UpdateProductRangeResponse) => {
        if (x.success) {
          this.productService.RefreshData();
          this.snackBar.open(`${this.rangeName} range updated!`, 'OK', { duration: 2000 });
          this.baseInRangeIds = this.inRange.map(x => x.id!);
          this.checkIfRangeModified();
        } else {
          this.snackBar.open(`Error updating ${this.rangeName} Range!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error updating ${this.rangeName} Range!`, 'OK', { duration: 2000 });
      });
  };
  
  updateProductRangeAuthorisedCompany() {
    let request = new UpdateProductRangeAuthorisedCompanyRequest();
    request.rangeId = this.id;
    request.companyId = this.selectedCompany?.id;

    request.companyIds = this.getOtherCompanyIds();

    this.rangeService.UpdateProductRangeAuthorisedCompany(request)
      .then((x: UpdateProductRangeAuthorisedCompanyResponse) => {
        if (x.success) {
          this.productService.RefreshData();
          this.snackBar.open(`${this.rangeName} range updated!`, 'OK', { duration: 2000 });
          this.basePartnershipIds = this.getOtherCompanyIds();
          this.checkIfPartnershipModified();
        } else {
          this.snackBar.open(`Error updating ${this.rangeName} Range!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error updating ${this.rangeName} Range!`, 'OK', { duration: 2000 });
      });
  };

  private loadRange() {
    if (!this.id || !this.selectedCompany?.id) {
      return Promise.resolve();
    }
    let getRangeRequest = new GetRangeRequest();
    getRangeRequest.id = this.id;
    getRangeRequest.companyId = this.selectedCompany.id;

   return this.rangeService.GetRange(getRangeRequest)
      .then((response: GetRangeResponse) => {
        if (response.success && response.range) {
          if (response.range?.companyId !== this.selectedCompany?.id) {
            this.router.navigate([`products/ranges`]);
            return Promise.resolve();
          }
          this.rangeName = response.range.name;
          this.selectedRange = response.range;
          this.rangeForm.patchValue(response.range);
          if (!this.canBeEdited(response.range)) {
            this.rangeForm.controls.name.disable();
            this.toggleShareMode();
          }
          this.baseInRangeIds = response.range.productIds ?? [];
        } else {
          this.snackBar.open(`Could not fetch the range. Refresh Page.`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Could not fetch the range. Refresh Page.`, 'OK', { duration: 2000 });
      });
  };

  private editRange() {
    if (!this.id || !this.selectedCompany?.id) {
      return Promise.resolve();
    }
    let request = new UpdateRangeRequest();
    request.rangeId = this.id;
    request.name = this.rangeForm.get("name")?.value;
    request.companyId = this.selectedCompany.id;

    return this.rangeService.UpdateRange(request)
      .then((x: UpdateRangeResponse) => {
        if (x.success) {
          this.snackBar.open(`${request.name} range updated!`, 'OK', { duration: 2000 });
          this.router.navigate([`products/ranges`]);
        } else {
          this.snackBar.open(`Error: ${x.errorMessages}!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error!`, 'OK', { duration: 2000 });
      });
  }

  showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }

  loadProducts() {
    if (!this.selectedCompany?.id) {
      return;
    }
    this.loading = true;
  
    return this.productService.LoadProducts(this.selectedCompany.id, this.selectedCompany.id)
      .then((products: Product2[]) => {
        products.forEach((x: Product2) => {
          let productForList: IListProduct2 = {
            ...x, 
            selected: false,
            filtered: false
          };
          if (!x.disabled) {
            if (this.baseInRangeIds.find(x => x === productForList.id)) {
              this.inRange.push(productForList);
            } else {
              this.outRange.push(productForList);
            }
          }
        });
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
      });
  }

  loadPartnerships() {
    this.loading = true;

    let request = new ListPartnershipsRequest();
    request.pageNumber = 1;
    request.pageSize = 25;
    request.companyId = this.selectedCompany?.id;
    request.partnershipStatuses = [PartnershipStatus.Approved, PartnershipStatus.Reconnected];

    return this.partnershipService.ListPartnerships(request)
      .then((response: ListPartnershipsResponse) => {
        if (response.success) {
          this.partnerships = response.partnerships?.map(x => {
            let partnership = x as IListPartnership;
            let otherCompanyId = this.partnershipService.getOtherCompanyId(x, this.selectedCompany?.id ?? "");
            partnership.selected = this.selectedRange?.rangeAuthorisedCompanies?.some(x => x.companyId === otherCompanyId) ?? false;
            return partnership;
          }) ?? [];
          this.basePartnershipIds = this.getOtherCompanyIds();
        }
        this.loading = false;
      })
      .catch((err: any) => {
        //
      });
  }

  private getOtherCompanyIds(): string[] {
    return this.partnerships
      .filter(x => x.selected)
      .map(x => this.partnershipService.getOtherCompanyId(x, this.selectedCompany?.id ?? "") as string) ?? []
  };

  private addRange() {
    let request = new CreateRangeRequest();
    request.name = this.rangeForm.get("name")?.value;
    request.companyId = this.selectedCompany?.id;
    request.resourceType = ResourceType.Product;

    this.rangeService.CreateRange(request)
      .then((x: CreateRangeResponse) => {
        if (x.success && x.range) {
          this.snackBar.open(`${x.range?.name} added!`, 'OK', { duration: 2000 });
          this.rangeForm.addControl("id", new FormControl(''));
          this.rangeForm.patchValue(x.range);
          this.isAddMode = false;
          this.router.navigate([`products/ranges`, x.range?.id]);
        } else {
          this.snackBar.open(`Error: ${x.errorMessages}!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Error!`, 'OK', { duration: 2000 });
      });
  };

  
}

export interface IListPartnership extends Partnership {
  selected: boolean;
  filtered: boolean;
}